import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';

import PropTypes from 'prop-types';
import isURL from 'validator/es/lib/isURL';
import isEmail from 'validator/lib/isEmail';

import { logoUrl } from '../../utils';
import { getTablePDFSubtitle, onFormatRows } from './utils';

import { SAFE_PDF_ROWS_PER_DOC_NUMBER } from './constants';

const TableComponent = ({ data, styles }) => (
  <View>
    {data?.map((row, i) => (
      <View style={styles.tableRow} key={i}>
        {row?.map((cell, i) => (
          <View
            style={i === 0 ? styles.firstTableCol : styles.tableCol}
            key={i}
            wrap={false}
          >
            {isURL(cell) && !isEmail(cell) ? (
              <Image
                src={{
                  uri: cell,
                  method: 'GET',
                  headers: [['Cache-Control', 'no-cache']],
                  body: '',
                }}
                style={{ margin: 3 }}
              />
            ) : (
              <Text style={styles.tableCell}>{cell}</Text>
            )}
          </View>
        ))}
      </View>
    ))}
  </View>
);

TableComponent.propTypes = {
  data: PropTypes.instanceOf(Array),
  styles: PropTypes.instanceOf(Object).isRequired,
};

const HeaderComponent = ({ headers, styles }) => (
  <View style={styles.tableRow} fixed>
    {headers.map((header, i) => (
      <View
        style={i === 0 ? styles.firstTableColHeader : styles.tableColHeader}
        key={header + i}
      >
        <Text style={styles.tableCellHeader}>{header}</Text>
      </View>
    ))}
  </View>
);

HeaderComponent.propTypes = {
  headers: PropTypes.instanceOf(Array).isRequired,
  styles: PropTypes.instanceOf(Object).isRequired,
};

export const TablePDFTemplate = ({ title, tableData }) => {
  const { headers = [], rows = [] } = tableData;
  const subtitle = getTablePDFSubtitle();

  const formattedRows = onFormatRows(rows);
  const COLUMNS_NUMBER = headers.length;
  const COLUMN_WIDTH = COLUMNS_NUMBER ? 100 / COLUMNS_NUMBER : 0;
  const BORDER_COLOR = '#bfbfbf';
  const BORDER_STYLE = 'solid';

  const getTableCellFontSize = () => {
    let fontSize = 10;
    if (COLUMNS_NUMBER > 10) {
      fontSize = 6;
    } else if (COLUMNS_NUMBER > 8) {
      fontSize = 7;
    } else if (COLUMNS_NUMBER > 6) {
      fontSize = 8;
    }
    return fontSize;
  };

  const styles = StyleSheet.create({
    body: {
      padding: 20,
      paddingBottom: 25,
    },
    logo: {
      marginVertical: 8,
      marginHorizontal: 'auto',
      height: 25,
    },
    title: {
      textAlign: 'center',
      fontSize: 18,
      marginBottom: 5,
    },
    subtitle: { textAlign: 'center', fontSize: 10, marginBottom: 20 },
    table: {
      width: 'auto',
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0,
      borderLeftWidth: 0,
    },
    tableRow: {
      margin: 'auto',
      flexDirection: 'row',
    },
    tableColHeader: {
      width: `${COLUMN_WIDTH}%`,
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
      backgroundColor: '#eee',
    },
    firstTableColHeader: {
      width: `${COLUMN_WIDTH}%`,
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderTopWidth: 0,
      backgroundColor: '#eee',
    },
    tableCol: {
      width: `${COLUMN_WIDTH}%`,
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
    },
    firstTableCol: {
      width: `${COLUMN_WIDTH}%`,
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderTopWidth: 0,
    },
    tableCellHeader: {
      margin: 2,
      fontSize: getTableCellFontSize(),
    },
    tableCell: {
      margin: 3,
      fontSize: getTableCellFontSize(),
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 8,
      bottom: 10,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey',
    },
  });

  const safeRowsLimit = SAFE_PDF_ROWS_PER_DOC_NUMBER;
  // #1
  const firstRowsSet = formattedRows?.slice(0, safeRowsLimit);
  const restRows = [];
  // #2
  if (formattedRows?.length > safeRowsLimit) {
    for (let i = safeRowsLimit; i < formattedRows.length; i += safeRowsLimit) {
      restRows.push(formattedRows.slice(i, i + safeRowsLimit));
    }
  }

  return (
    <Document>
      <Page orientation="landscape" style={styles.body}>
        <Image style={styles.logo} src={logoUrl} allowDangerousPaths />
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>
        <View style={styles.table}>
          <HeaderComponent headers={headers} styles={styles} />
          <TableComponent data={firstRowsSet} styles={styles} />
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      {restRows &&
        restRows.map((rowSet, i) => (
          <Page orientation="landscape" style={styles.body} key={i}>
            <View style={styles.table}>
              <HeaderComponent headers={headers} styles={styles} />
              <TableComponent data={rowSet} styles={styles} />
            </View>
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) =>
                `${pageNumber} / ${totalPages}`
              }
              fixed
            />
          </Page>
        ))}
    </Document>
  );
};

TablePDFTemplate.propTypes = {
  title: PropTypes.string,
  tableData: PropTypes.instanceOf(Object),
};
